import React, { memo } from 'react';
import TimeSlotRow from './TimeSlotRow';

/**
 * LiveBox — The active 60-second countdown pane.
 *
 * Layout (3 sections):
 *   ROW 1:  OPEN(blue) / CURRENT price + delta  — overflow:hidden, no scroll
 *   ROW 2:  Countdown timer                      — own row, always fully visible
 *   ROW 3:  Original TimeSlotRow (bet interface)
 */
const LiveBox = memo(({
    coin,
    slot,
    countdown,
    liveStartPrice,
    isPaused,
    betState,
    getSentimentRatio,
    getPayoutRatio,
    handleDotClick,
    handleBetChange,
    submitBet,
    setBets,
    currentPrice,
    priceFlash,
    poolTotal
}) => {
    if (!slot) return null;

    const hasFinished = countdown === 0;
    const diff = (currentPrice && liveStartPrice) ? (currentPrice - liveStartPrice) : null;

    return (
        <div className={`overflow-hidden transition-all border-b border-white/10 bg-gray-800 ${isPaused ? 'border-red-500/30' : ''}`}>

            {/* ROW 1: OPEN / CURRENT price + delta — never scrolls, truncates if needed */}
            <div className={`flex items-center px-3 py-1.5 border-b border-white/5 overflow-hidden min-w-0 ${isPaused ? 'bg-red-500/10' : ''}`}>
                <span className="font-mono text-lg font-normal text-blue-400 tracking-tight shrink truncate min-w-0">
                    {liveStartPrice
                        ? liveStartPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '—'}
                </span>
                <span className="font-mono text-lg font-normal text-white/40 mx-1 flex-shrink-0">/</span>
                <span className={`font-mono text-lg font-normal tracking-tight transition-colors duration-150 shrink truncate min-w-0 ${
                    priceFlash === 'flash-green' ? 'text-green-300'
                    : priceFlash === 'flash-red' ? 'text-red-300'
                    : hasFinished
                        ? (currentPrice >= liveStartPrice ? 'text-green-400' : 'text-red-400')
                        : 'text-white'
                }`}>
                    {currentPrice
                        ? currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '—'}
                </span>
                {diff !== null && (
                    <span className={`font-mono text-2xl font-normal ml-2 tracking-tight tabular-nums flex-shrink-0 ${
                        diff > 0 ? 'text-green-400' : diff < 0 ? 'text-red-400' : 'text-gray-400'
                    }`}>
                        ({diff >= 0 ? '+' : ''}{diff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                    </span>
                )}
            </div>

            {/* ROW 2: Countdown timer — dedicated row, always 100% visible, zero scroll */}
            <div className={`flex items-center justify-between px-3 py-1 border-b border-white/5 ${isPaused ? 'bg-red-500/10' : ''}`}>
                {isPaused ? (
                    <span className="text-[10px] text-red-500 font-black uppercase tracking-widest bg-red-500/10 px-2 py-0.5 border border-red-500/20 rounded-sm">
                        Stopped
                    </span>
                ) : (
                    <span className={`font-mono text-2xl font-bold text-red-500 tracking-wider tabular-nums bg-white rounded px-2 py-0.5 ${countdown <= 10 ? 'animate-pulse' : ''}`}>
                        {String(countdown).padStart(2, '0')}
                    </span>
                )}
                <span className="text-[9px] uppercase tracking-widest text-white/25 font-black">Live</span>
            </div>

            {/* ROW 3: Original TimeSlotRow — exactly as bet was placed */}
            <div>
                <TimeSlotRow
                    coinId={coin.id}
                    slot={slot}
                    isNextFive={false}
                    isLocked={true}
                    isLive={true}
                    betState={betState}
                    getSentimentRatio={getSentimentRatio}
                    getPayoutRatio={getPayoutRatio}
                    handleDotClick={handleDotClick}
                    handleBetChange={handleBetChange}
                    submitBet={submitBet}
                    setBets={setBets}
                    poolTotal={poolTotal}
                />
            </div>
        </div>
    );
});

export default LiveBox;
